import { useMutation, useQuery } from "@apollo/client";
import Button from "components/base/Button";
import { FileSelector, Input, Switcher, Text } from "components/base/Form";
import { Alert } from "components/base/Toast";
import { useState, useEffect } from "react";
import { FiCopy, FiExternalLink } from "react-icons/fi";
import { InlineSpinner } from "components/base/Spinner";
import {
  FETCH_VIDEO,
  UPDATE_VIDEO,
  CREATE_VIDEO_SOURCE,
  UPDATE_VIDEO_SOURCE,
  DELETE_VIDEO_SOURCE,
} from "./graphql";

const VideoForm = ({ video: initialVideo, hide }) => {
  const { data, loading: fetchLoading } = useQuery(FETCH_VIDEO, {
    variables: { id: initialVideo.id },
  });

  const video = data?.video || initialVideo;

  const [name, setName] = useState(video?.name || "");
  const [description, setDescription] = useState(video?.description || "");
  const [active, setActive] = useState(video?.active || false);
  const [childLoading, setChildLoading] = useState(false);

  // 当 video 数据加载完成后更新 state
  useEffect(() => {
    if (video) {
      setName(video.name || "");
      setDescription(video.description || "");
      setActive(video.active || false);
    }
  }, [video?.id]);

  const [createVideoSource, createVideoSourceRes] = useMutation(
    CREATE_VIDEO_SOURCE,
    {
      onCompleted() {
        Alert("success", "Video uploaded successfully.");
      },
      onError(error) {
        Alert("error", error.message);
      },
      refetchQueries: [{ query: FETCH_VIDEO, variables: { id: video.id } }],
    },
  );

  const [updateVideo, updateVideoRes] = useMutation(UPDATE_VIDEO, {
    onCompleted() {
      Alert("success", "Video updated successfully.");
    },
    onError(error) {
      Alert("error", error.message);
    },
  });

  const isUploading =
    createVideoSourceRes.loading || updateVideoRes.loading || childLoading;

  function uploadFile(file) {
    createVideoSource({ variables: { videoId: video.id, file } });
  }

  if (fetchLoading) {
    return (
      <div className="px-10 pb-10 flex items-center justify-center py-20">
        <InlineSpinner size={40} />
      </div>
    );
  }

  if (!video) {
    return (
      <div className="px-10 pb-10 py-20 text-center text-gray-500">
        Video not found
      </div>
    );
  }

  return (
    <div className="px-10 pb-10 space-y-6 relative">
      {/* Uploading Overlay */}
      {isUploading && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 flex flex-col items-center space-y-4">
            <InlineSpinner size={40} />
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {createVideoSourceRes.loading
                ? "Uploading video..."
                : updateVideoRes.loading
                  ? "Updating..."
                  : "Replacing video source..."}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Please do not close this window
            </p>
          </div>
        </div>
      )}

      {/* Video Info Section */}
      <div className="space-y-4">
        <div className="flex items-center space-x-3">
          <label className="w-24 text-sm font-medium">Name:</label>
          <Input
            className="flex-1"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="flex items-start space-x-3">
          <label className="w-24 text-sm font-medium pt-2">Description:</label>
          <Text
            placeholder="Enter description"
            className="flex-1"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className="flex items-center space-x-3">
          <label className="w-24 text-sm font-medium">Active:</label>
          <Switcher isOn={active} onChange={() => setActive((prev) => !prev)} />
        </div>

        <div className="flex justify-end">
          <Button
            primary
            loading={updateVideoRes.loading}
            disabled={updateVideoRes.loading}
            onClick={() =>
              updateVideo({
                variables: { id: video.id, name, description, active },
              }).then(() => {
                hide();
              })
            }
          >
            Save Changes
          </Button>
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-dashed border-gray-300 dark:border-gray-600"></div>

      {/* Video Sources Section */}
      <div>
        <h4 className="text-lg font-semibold mb-4">Video Sources</h4>

        {video.sources.length === 0 && !createVideoSourceRes.loading && (
          <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded text-center">
            <p className="text-sm text-yellow-700 dark:text-yellow-300">
              📹 No video sources yet. Please upload a video file below.
            </p>
          </div>
        )}

        <div className="space-y-4">
          {video.sources.map((source) => (
            <VideoSource
              key={source.id}
              source={source}
              videoId={video.id}
              onLoadingChange={setChildLoading}
            />
          ))}
        </div>

        <div className="mt-6">
          <FileSelector
            title="+ Add New Source"
            accept="video"
            uploading={createVideoSourceRes.loading}
            disabled={isUploading}
            onChange={(e) => {
              uploadFile(e.target.files[0]);
              e.target.value = null;
            }}
          />
        </div>
      </div>
    </div>
  );
};

const VideoSource = ({ source, videoId, onLoadingChange }) => {
  const [updateVideoSource, updateVideoSourceRes] = useMutation(
    UPDATE_VIDEO_SOURCE,
    {
      onCompleted() {
        Alert("success", "Video source updated.");
      },
      onError(error) {
        Alert("error", error.message);
      },
      refetchQueries: [{ query: FETCH_VIDEO, variables: { id: videoId } }],
    },
  );

  useEffect(() => {
    onLoadingChange(updateVideoSourceRes.loading);
  }, [updateVideoSourceRes.loading, onLoadingChange]);

  const [deleteVideoSource, deleteVideoSourceRes] = useMutation(
    DELETE_VIDEO_SOURCE,
    {
      variables: { id: source.id },
      onCompleted() {
        Alert("success", "Video source deleted.");
      },
      onError(error) {
        Alert("error", error.message);
      },
      refetchQueries: ["FETCH_ALL_VIDEOS"],
    },
  );

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      Alert("success", `${label} copied to clipboard!`);
    });
  };

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <div className="flex">
        {/* Left: Thumbnail */}
        <div className="w-64 flex-shrink-0 bg-gray-100 dark:bg-gray-700">
          {source.thumbSrc ? (
            <img
              className="w-full h-full object-cover"
              src={source.thumbSrc}
              alt={source.filename}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-400">
              <span className="text-sm">No thumbnail</span>
            </div>
          )}
        </div>

        {/* Right: Info */}
        <div className="flex-1 p-4">
          <div className="space-y-3">
            {/* Filename */}
            <div>
              <h5 className="text-base font-semibold text-gray-900 dark:text-white">
                {source.filename}
              </h5>
              {source.height && (
                <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded">
                  {source.height}p
                </span>
              )}
            </div>

            {/* Source Download */}
            {source.sourceUrl && (
              <div className="flex items-center space-x-2">
                <span className="text-xs text-gray-500 w-24">Source:</span>
                <div className="flex-1 flex items-center space-x-2">
                  <a
                    href={source.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 dark:text-blue-400 hover:underline truncate flex items-center space-x-1"
                  >
                    <span className="truncate max-w-md">
                      {source.sourceUrl}
                    </span>
                    <FiExternalLink className="flex-shrink-0" size={14} />
                  </a>
                  <button
                    onClick={() =>
                      copyToClipboard(source.sourceUrl, "Source URL")
                    }
                    className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                    title="Copy URL"
                  >
                    <FiCopy size={14} />
                  </button>
                </div>
              </div>
            )}

            {/* Preview Link */}
            {source.convertedVideoSrc && (
              <div className="flex items-center space-x-2">
                <span className="text-xs text-gray-500 w-24">Preview:</span>
                <div className="flex-1 flex items-center space-x-2">
                  <a
                    href={source.convertedVideoSrc}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 dark:text-blue-400 hover:underline truncate flex items-center space-x-1"
                  >
                    <span className="truncate max-w-md">
                      {source.convertedVideoSrc}
                    </span>
                    <FiExternalLink className="flex-shrink-0" size={14} />
                  </a>
                  <button
                    onClick={() =>
                      copyToClipboard(source.convertedVideoSrc, "Preview URL")
                    }
                    className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                    title="Copy URL"
                  >
                    <FiCopy size={14} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom: Actions */}
      <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-750 px-4 py-3 flex justify-end space-x-4">
        <FileSelector
          title="Replace"
          uploading={updateVideoSourceRes.loading}
          onChange={(e) => {
            updateVideoSource({
              variables: {
                id: source.id,
                file: e.target.files[0],
              },
            });
            e.target.value = null;
          }}
        />
        <Button
          danger
          loading={deleteVideoSourceRes.loading}
          disabled={deleteVideoSourceRes.loading}
          onClick={() => {
            if (window.confirm("Do you want to delete this video source?")) {
              deleteVideoSource();
            }
          }}
        >
          Delete
        </Button>
      </div>
    </div>
  );
};

export default VideoForm;
